import {
  changeRoleStatus,
  createRole,
  getRoleCatalog,
  RoleCatalogApiError,
  updateRole,
  type RoleCatalogItem,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalCard,
  CanonicalDefinitionList,
  CanonicalFilterBar,
  CanonicalFilterField,
  CanonicalModal,
  CanonicalNotice,
  CanonicalPageHeader,
  CanonicalStatus,
  DataTable,
  DataTableFooter,
  FilterActions,
  FormActions,
  FormSection,
  IconButton,
  PageSizeSelector,
  Pagination,
  PermissionGate,
  ResultsCounter,
  RowActions,
  SearchField,
  SelectField,
  StatePanel,
  TextAreaField,
  TextField,
  canonicalStates,
  formatDateTime,
  type DataColumn,
} from '@urbanik/ui';
import { Eye, FunnelX, ListFilter, Pencil, Plus, Power, PowerOff, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { apiBase } from './api-base';
import { RolePermissionsEditor } from './RolePermissionsEditor';
type ActiveFilter = 'all' | 'active' | 'inactive';
type Editor = Readonly<{ mode: 'create' } | { mode: 'edit'; role: RoleCatalogItem }>;
type PendingStatus = Readonly<{ role: RoleCatalogItem; active: boolean }>;
export function RolesView({ permissions }: { permissions: readonly string[] }) {
  const [items, setItems] = useState<readonly RoleCatalogItem[]>([]),
    [search, setSearch] = useState(''),
    [active, setActive] = useState<ActiveFilter>('all'),
    [page, setPage] = useState(1),
    [pageSize, setPageSize] = useState(10),
    [total, setTotal] = useState(0),
    [pages, setPages] = useState(1),
    [state, setState] = useState<'loading' | 'ready' | 'empty' | 'error'>('loading'),
    [reload, setReload] = useState(0),
    [selected, setSelected] = useState<RoleCatalogItem | null>(null),
    [permissionRole, setPermissionRole] = useState<RoleCatalogItem | null>(null),
    [editor, setEditor] = useState<Editor | null>(null),
    [pendingStatus, setPendingStatus] = useState<PendingStatus | null>(null),
    [reason, setReason] = useState(''),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState<{ message: string; tone: 'success' | 'error' } | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    getRoleCatalog(
      apiBase,
      { search, active: active === 'all' ? 'all' : active === 'active', page, pageSize },
      controller.signal,
    )
      .then((result) => {
        setItems(result.items);
        setTotal(result.total);
        setPages(result.totalPages);
        setState(result.items.length ? 'ready' : 'empty');
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (error instanceof RoleCatalogApiError && error.kind === 'unauthorized')
          window.location.hash = '#login';
        setState('error');
      });
    return () => controller.abort();
  }, [search, active, page, pageSize, reload]);
  const report = (error: unknown) =>
    setNotice({
      message: error instanceof Error ? error.message : 'No pudimos completar la operación.',
      tone: 'error',
    });
  const refresh = (message: string) => {
    setNotice({ message, tone: 'success' });
    setEditor(null);
    setPendingStatus(null);
    setReason('');
    setReload((value) => value + 1);
  };
  async function submitRole(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const input = {
      name: String(data.get('name') ?? ''),
      description: String(data.get('description') ?? ''),
      reason: String(data.get('reason') ?? ''),
    };
    setBusy(true);
    try {
      if (editor?.mode === 'edit')
        await updateRole(apiBase, editor.role.id, {
          ...input,
          expectedVersion: editor.role.version,
        });
      else await createRole(apiBase, input);
      refresh(
        editor?.mode === 'edit' ? 'Rol actualizado correctamente.' : 'Rol creado correctamente.',
      );
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  }
  async function confirmStatus() {
    if (!pendingStatus || !reason.trim()) return;
    setBusy(true);
    try {
      await changeRoleStatus(apiBase, pendingStatus.role.id, {
        active: pendingStatus.active,
        expectedVersion: pendingStatus.role.version,
        reason: reason.trim(),
      });
      refresh(
        pendingStatus.active ? 'Rol activado.' : 'Rol desactivado y sesiones afectadas revocadas.',
      );
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  }
  const columns = useMemo<readonly DataColumn<RoleCatalogItem>[]>(
    () => [
      {
        id: 'name',
        header: 'Rol',
        accessor: (role) => role.name,
        sortable: true,
        cell: (role) => (
          <>
            <strong>{role.name}</strong>
            <br />
            <small>{role.code}</small>
          </>
        ),
      },
      {
        id: 'status',
        header: 'Estado',
        accessor: (role) => (role.isActive ? 'Activo' : 'Inactivo'),
        cell: (role) => (
          <CanonicalStatus tone={role.isActive ? 'success' : 'neutral'}>
            {role.isActive ? 'Activo' : 'Inactivo'}
          </CanonicalStatus>
        ),
      },
      {
        id: 'type',
        header: 'Tipo',
        accessor: (role) => (role.isSystem ? 'Sistema' : 'Administrativo'),
      },
      { id: 'users', header: 'Usuarios', accessor: (role) => String(role.userCount) },
      { id: 'permissions', header: 'Permisos', accessor: (role) => String(role.permissionCount) },
      {
        id: 'actions',
        header: 'Acciones',
        accessor: () => '',
        cell: (role) => (
          <RowActions>
            <IconButton
              type="button"
              label={`Ver detalle de ${role.name}`}
              onClick={() => setSelected(role)}
            >
              <Eye aria-hidden="true" />
            </IconButton>
            <PermissionGate permission="identity.permissions.read" permissions={permissions}>
              <IconButton
                type="button"
                label={'Administrar permisos de ' + role.name}
                onClick={() => setPermissionRole(role)}
              >
                <ShieldCheck aria-hidden="true" />
              </IconButton>
            </PermissionGate>
            <PermissionGate permission="identity.roles.update" permissions={permissions}>
              <IconButton
                type="button"
                label={`Editar ${role.name}`}
                onClick={() => setEditor({ mode: 'edit', role })}
              >
                <Pencil aria-hidden="true" />
              </IconButton>
            </PermissionGate>
            <PermissionGate permission="identity.roles.status.manage" permissions={permissions}>
              <IconButton
                type="button"
                label={`${role.isActive ? 'Desactivar' : 'Activar'} ${role.name}`}
                disabled={role.isSystem}
                onClick={() => {
                  setReason('');
                  setPendingStatus({ role, active: !role.isActive });
                }}
              >
                {role.isActive ? <PowerOff aria-hidden="true" /> : <Power aria-hidden="true" />}
              </IconButton>
            </PermissionGate>
          </RowActions>
        ),
      },
    ],
    [permissions],
  );
  return (
    <div>
      <CanonicalPageHeader
        title="Catálogo de roles"
        description="Administra roles del sistema. La selección editable de permisos corresponde a CR-M02-001.4."
        actions={
          <PermissionGate permission="identity.roles.create" permissions={permissions}>
            <Button
              type="button"
              variant="primary"
              icon={<Plus aria-hidden="true" />}
              onClick={() => setEditor({ mode: 'create' })}
            >
              Crear rol
            </Button>
          </PermissionGate>
        }
      />
      <CanonicalNotice
        message={notice?.message ?? ''}
        tone={notice?.tone ?? 'success'}
        onDismiss={() => setNotice(null)}
      />
      <CanonicalCard title="Roles persistidos">
        <CanonicalFilterBar>
          <SearchField
            value={search}
            label="Buscar roles"
            placeholder="Nombre, código o descripción"
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
          />
          <CanonicalFilterField icon={<ListFilter aria-hidden="true" />}>
            <SelectField
              label="Estado"
              value={active}
              options={[
                { value: 'all', label: 'Todos' },
                { value: 'active', label: 'Activos' },
                { value: 'inactive', label: 'Inactivos' },
              ]}
              onValueChange={(value) => {
                setActive(value as ActiveFilter);
                setPage(1);
              }}
            />
          </CanonicalFilterField>
          {search || active !== 'all' ? (
            <FilterActions>
              <Button
                type="button"
                icon={<FunnelX aria-hidden="true" />}
                onClick={() => {
                  setSearch('');
                  setActive('all');
                  setPage(1);
                }}
              >
                Limpiar filtros
              </Button>
            </FilterActions>
          ) : null}
        </CanonicalFilterBar>
        {state === 'loading' ? (
          <StatePanel state={canonicalStates.find((item) => item.kind === 'loading')!} />
        ) : null}
        {state === 'error' ? (
          <StatePanel
            state={canonicalStates.find((item) => item.kind === 'error')!}
            onAction={() => setReload((value) => value + 1)}
          />
        ) : null}
        {state === 'empty' ? (
          <StatePanel state={canonicalStates.find((item) => item.kind === 'empty')!} />
        ) : null}
        {state === 'ready' ? (
          <DataTable
            paginate={false}
            rows={items}
            columns={columns}
            visibleColumnIds={new Set(columns.map((column) => column.id))}
            sort={{ columnId: 'name', direction: 'ascending' }}
            onSort={() => undefined}
            empty={<StatePanel state={canonicalStates.find((item) => item.kind === 'empty')!} />}
          />
        ) : null}
        <DataTableFooter>
          <ResultsCounter
            from={items.length ? (page - 1) * pageSize + 1 : 0}
            to={(page - 1) * pageSize + items.length}
            total={total}
            noun="roles"
          />
          <Pagination page={page} pages={pages} onChange={setPage} />
          <PageSizeSelector
            value={pageSize}
            options={[10, 25, 50]}
            onChange={(value) => {
              setPageSize(value);
              setPage(1);
            }}
          />
        </DataTableFooter>
      </CanonicalCard>
      <RolePermissionsEditor
        role={permissionRole}
        onClose={() => setPermissionRole(null)}
        canManage={permissions.includes('identity.role_permissions.manage')}
      />
      <CanonicalModal
        open={selected !== null}
        title={selected?.name ?? 'Detalle de rol'}
        eyebrow="Detalle del catálogo"
        description={selected?.description ?? ''}
        onClose={() => setSelected(null)}
      >
        {selected ? (
          <CanonicalDefinitionList
            label="Datos del rol"
            items={[
              { label: 'Código', value: selected.code },
              { label: 'Estado', value: selected.isActive ? 'Activo' : 'Inactivo' },
              { label: 'Tipo', value: selected.isSystem ? 'Sistema protegido' : 'Administrativo' },
              { label: 'Usuarios asignados', value: selected.userCount },
              { label: 'Permisos asignados', value: selected.permissionCount },
              { label: 'Versión', value: selected.version },
              { label: 'Actualizado', value: formatDateTime(selected.updatedAt) },
            ]}
          />
        ) : null}
      </CanonicalModal>
      <CanonicalModal
        open={editor !== null}
        title={editor?.mode === 'edit' ? 'Editar rol' : 'Crear rol'}
        eyebrow="Catálogo administrativo"
        description="Los permisos del rol se administrarán únicamente en CR-M02-001.4."
        size="medium"
        onClose={() => !busy && setEditor(null)}
      >
        {editor ? (
          <form onSubmit={submitRole}>
            <FormSection
              title="Identidad del rol"
              description="El sistema genera un código único e inmutable al guardar."
            >
              <TextField
                name="name"
                label="Nombre"
                required
                minLength={2}
                maxLength={100}
                defaultValue={editor.mode === 'edit' ? editor.role.name : ''}
              />
              <CanonicalDefinitionList
                label="Identificador del rol"
                items={[
                  {
                    label: 'Código',
                    value:
                      editor.mode === 'edit'
                        ? editor.role.code
                        : 'Se generará automáticamente al guardar.',
                  },
                ]}
              />
              <TextAreaField
                name="description"
                label="Descripción"
                maxLength={500}
                rows={3}
                defaultValue={editor.mode === 'edit' ? editor.role.description : ''}
              />
              <TextAreaField
                name="reason"
                label="Motivo"
                required
                minLength={3}
                maxLength={500}
                rows={3}
                placeholder={
                  editor.mode === 'edit'
                    ? 'Ej.: Se aclara el alcance del rol para incluir la conciliación de datos.'
                    : 'Ej.: Se necesita para gestionar socios sin acceso a configuración.'
                }
                hint={
                  editor.mode === 'edit'
                    ? 'Indica qué cambió y por qué es necesario.'
                    : 'Explica para qué responsabilidad o proceso se necesita este rol.'
                }
              />
            </FormSection>
            <FormActions>
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => setEditor(null)}
              >
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={busy}>
                {busy ? 'Guardando…' : 'Guardar rol'}
              </Button>
            </FormActions>
          </form>
        ) : null}
      </CanonicalModal>
      <CanonicalModal
        open={pendingStatus !== null}
        title={pendingStatus?.active ? 'Activar rol' : 'Desactivar rol'}
        eyebrow="Confirmación requerida"
        description={pendingStatus ? `${pendingStatus.role.name} · ${pendingStatus.role.code}` : ''}
        size="medium"
        onClose={() => !busy && setPendingStatus(null)}
      >
        <TextAreaField
          label="Motivo del cambio"
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          hint={
            pendingStatus?.active
              ? 'Explica por qué debe volver a estar disponible este rol.'
              : 'Explica por qué se retira el acceso. Las sesiones afectadas serán revocadas.'
          }
          rows={3}
        />
        <FormActions>
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => setPendingStatus(null)}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant={pendingStatus?.active ? 'primary' : 'danger'}
            disabled={busy || !reason.trim()}
            onClick={() => void confirmStatus()}
          >
            {busy ? 'Procesando…' : 'Confirmar'}
          </Button>
        </FormActions>
      </CanonicalModal>
    </div>
  );
}
